import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

import { pickBy } from "lodash";

import { useSetLocation } from "@services/reactRouterDom";
import unflatten from "@utils/unflatten";

const flattenObject = (obj, keys: string[] = [], separator = ".") =>
  Object.keys(obj).reduce((memo, prop) => {
    const value = obj[prop];
    const nextKeys = keys.concat([prop]);
    const isObject =
      Object.prototype.toString.call(value) === "[object Object]";

    if (isObject) {
      return {
        ...memo,
        ...flattenObject(value, nextKeys, separator),
      };
    }

    return { ...memo, [nextKeys.join(separator)]: value };
  }, {});

const encodeArrays = (obj: Record<string, unknown>): Record<string, string> =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) =>
      Array.isArray(value)
        ? [key + "[]", value.join(",")]
        : [key, String(value)]
    )
  );

const isEmptyFilterValue = (val: unknown) =>
  val === false ||
  val === null ||
  val === undefined ||
  val === "" ||
  (Array.isArray(val) && val.length === 0);

const encodeSearchParams = (data: Record<string, any>) => {
  // Before stringify so boolean false is dropped, not kept as `"false"`.
  const filterData = encodeArrays(
    pickBy(flattenObject(data), (val) => !isEmptyFilterValue(val))
  );

  return new URLSearchParams(filterData).toString();
};

export const encodeUrlWithSearchParams = (
  url: `/${string}`,
  params: Record<string, any>
): `/${string}` => {
  const query = encodeSearchParams(params);
  return (url + (query ? `?${query}` : "")) as `/${string}`;
};

const decodeArrays = (obj: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) =>
      key.endsWith("[]") ? [key.slice(0, -2), value.split(",")] : [key, value]
    )
  );

const decodeSearchParams = (searchParams: URLSearchParams) => {
  const obj = Object.fromEntries(searchParams.entries());
  return unflatten(decodeArrays(obj), ".");
};

const useFilterParams = (): [
  Record<string, any>,
  (filterObject: Record<string, any>) => void,
] => {
  const [searchParams] = useSearchParams();
  // setSearchParams overwrites the hash, so we need to use useSetLocation
  const setLocation = useSetLocation();

  const applyFilters = useCallback(
    (formData) => {
      const search = encodeSearchParams(formData).toString();
      setLocation({ search: `?${search}` }, { replace: true });
    },
    [setLocation]
  );

  const appliedFilters = useMemo(
    () => decodeSearchParams(searchParams),
    [searchParams]
  );

  return [appliedFilters, applyFilters];
};

export default useFilterParams;
